import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Stack } from '@mantine/core';
import { OrbitControls } from '@react-three/drei';
import { Canvas, useThree } from '@react-three/fiber';

import { useChartContext } from '@/contexts/ChartContext';
import { useKeyboardCameraControls } from '@/hooks/useKeyboardCameraControls';
import { getHandDataArray } from '@/lib/chartDataUtils';
import { useChartStore } from '@/store/chartStore';
import type { ChartData, HandData } from '@/types/chart';

import CameraControls from './CameraControls';
import GridLabels from './GridLabels';
import HandBar from './HandBar';
import HandDetailsPanel from './HandDetailsPanel';
import Legend from './Legend';

interface Chart3DProps {
  data: ChartData;
  controlPaneContent?: React.ReactNode;
}

interface ChartSceneProps extends Chart3DProps {
  selectedHand: HandData | null;
  onHandClick: (hand: HandData) => void;
  barSize: number;
  gap: number;
  maxHeight: number;
}

function CameraController({ resetTrigger, topDownTrigger }: { resetTrigger: number; topDownTrigger: number }) {
  const { camera, size } = useThree();
  const [controlsKey, setControlsKey] = useState(0);
  const controlsRef = useRef(null);
  const isMobile = size.width < 768;

  // Calculate camera distance based on viewport size to fit the chart
  const calculateCameraDistance = useCallback(
    (isTopDown: boolean) => {
      const aspect = size.width / size.height;
      const chartSize = 13; // Chart is roughly 13x13 units
      const fov = 50; // Field of view from camera config

      // Calculate distance needed to fit the chart in view
      const vFov = (fov * Math.PI) / 180;
      const heightDistance = chartSize / (2 * Math.tan(vFov / 2));
      const widthDistance = heightDistance / aspect;

      // Use the larger distance to ensure everything fits
      const paddingMultiplier = size.width < 768 ? 0.95 : 1.05; // Tighter on mobile
      const baseDistance = Math.max(heightDistance, widthDistance) * paddingMultiplier;

      if (isTopDown) {
        const topDownMultiplier = size.width < 768 ? 0.95 : 1.15;
        return Math.max(16, baseDistance * topDownMultiplier);
      }
      // For isometric view, we need both vertical and horizontal distance
      const isoMultiplier = size.width < 768 ? 0.6 : 0.75;
      const isoDistance = baseDistance * isoMultiplier;
      return Math.max(11, isoDistance);
    },
    [size],
  );

  // Reset camera when resetTrigger changes
  useEffect(() => {
    if (resetTrigger > 0) {
      const distance = calculateCameraDistance(false);
      camera.position.set(0, distance, distance);
      // Remount controls to clear momentum
      setControlsKey((prev) => prev + 1);
    }
  }, [resetTrigger, camera, calculateCameraDistance]);

  // Top-down view when topDownTrigger changes
  useEffect(() => {
    if (topDownTrigger > 0) {
      const distance = calculateCameraDistance(true);
      camera.position.set(0, distance, 0);
      // Remount controls to clear momentum
      setControlsKey((prev) => prev + 1);
    }
  }, [topDownTrigger, camera, calculateCameraDistance]);

  // Use custom keyboard controls hook
  useKeyboardCameraControls({ camera, controlsRef, controlsKey });

  return (
    <OrbitControls
      ref={controlsRef}
      key={controlsKey}
      enablePan={isMobile}
      enableZoom={true}
      enableRotate={true}
      minDistance={5}
      maxDistance={30}
      minPolarAngle={0}
      maxPolarAngle={Math.PI / 2 - 0.1}
      target={DEFAULT_CAMERA_TARGET}
      makeDefault
    />
  );
}

function ChartScene({ data, selectedHand, onHandClick, barSize, gap, maxHeight }: ChartSceneProps) {
  const colorScheme = useChartStore((state) => state.config.colorScheme);
  const useLogScale = useChartStore((state) => state.config.useLogScale);
  const showValues = useChartStore((state) => state.config.showValues);
  const segmentLayout = useChartStore((state) => state.config.segmentLayout);
  const segmentGap = useChartStore((state) => state.config.segmentGap);

  // Generate HandData array from grid
  const hands = useMemo(() => getHandDataArray(data), [data]);

  return (
    <group>
      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 20, 10]} intensity={1.0} />
      <directionalLight position={[-10, 10, -10]} intensity={0.4} />

      {/* Grid floor for reference */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]}>
        <planeGeometry args={[11, 11]} />
        <meshStandardMaterial color="#2a2a3e" />
      </mesh>

      {/* Hand bars */}
      {hands.map((handData) => (
        <HandBar
          key={handData.hand}
          handData={handData}
          chartData={data}
          colorScheme={colorScheme}
          maxHeight={maxHeight}
          barSize={barSize}
          gap={gap}
          isSelected={selectedHand?.hand === handData.hand}
          onClick={() => onHandClick(handData)}
          useLogScale={useLogScale}
          showValues={showValues}
          segmentLayout={segmentLayout}
          segmentGap={segmentGap}
        />
      ))}

      {/* Axis labels */}
      <GridLabels barSize={barSize} gap={gap} />
    </group>
  );
}

const DEFAULT_CAMERA_TARGET = [0, 0, 0] as const;

function ExportHandler({ chartName }: { chartName?: string }) {
  const { gl } = useThree();
  const { exportFn } = useChartContext();

  const exportChart = useCallback(
    (callback: (blob: Blob, filename: string) => void) => {
      const canvas = gl.domElement;
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = chartName ? `${chartName}-${timestamp}.png` : `poker-chart-${timestamp}.png`;

      canvas.toBlob((blob) => {
        if (!blob) return;
        callback(blob, filename);
      }, 'image/png');
    },
    [gl, chartName],
  );

  useEffect(() => {
    exportFn.set(exportChart);
  }, [exportChart, exportFn]);

  return null;
}

export default function Chart3D({ data, controlPaneContent }: Chart3DProps) {
  const [selectedHand, setSelectedHand] = useState<HandData | null>(null);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [topDownTrigger, setTopDownTrigger] = useState(0);
  const config = useChartStore((state) => state.config);

  const handleHandClick = (hand: HandData) => {
    // Toggle: if clicking the same hand, deselect it
    setSelectedHand((prev) => (prev?.hand === hand.hand ? null : hand));
  };

  const handleClosePanel = () => {
    setSelectedHand(null);
  };

  const handleResetCamera = () => {
    setResetTrigger((prev) => prev + 1);
  };

  const handleTopDownView = () => {
    setTopDownTrigger((prev) => prev + 1);
  };

  const showLegend = config.showLegend && data.categoryMapping !== undefined;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        handleClosePanel();
        break;
      case 'r':
      case 'R':
        // Only trigger if no modifier keys are pressed (avoid cmd+R, ctrl+R, shift+R)
        if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
          handleResetCamera();
          e.preventDefault();
        }
        break;
      case 't':
      case 'T':
        // Only trigger if no modifier keys are pressed
        if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
          handleTopDownView();
          e.preventDefault();
        }
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="application"
      style={{ width: '100%', height: '100%', position: 'relative', outline: 'none' }}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: role="application" makes this interactive
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label="3D poker hand chart. Use arrow keys or WASD to pan, shift + W/S to rotate pitch, shift + A/D to zoom, Q/E to rotate left/right, R to reset camera, T for top-down view, and Escape to close details panel."
    >
      <Canvas
        camera={{
          position: [0, 12, 12],
          fov: 50,
          near: 0.1,
          far: 100,
        }}
        gl={{ preserveDrawingBuffer: true, alpha: true }}
        onPointerMissed={handleClosePanel}
      >
        <ExportHandler chartName={data.name} />
        <ChartScene
          data={data}
          selectedHand={selectedHand}
          onHandClick={handleHandClick}
          barSize={config.barSize}
          gap={config.gap}
          maxHeight={config.maxHeight}
        />
        <CameraController resetTrigger={resetTrigger} topDownTrigger={topDownTrigger} />
      </Canvas>
      {/* Right panel container with flexible layout */}
      <Stack
        gap="sm"
        style={{
          position: 'absolute',
          top: 'max(12px, env(safe-area-inset-top, 12px))',
          right: 'max(12px, env(safe-area-inset-right, 12px))',
          width: 300,
          zIndex: 100,
        }}
      >
        {showLegend && <Legend chartData={data} />}
        {selectedHand && <HandDetailsPanel hand={selectedHand} chartData={data} onClose={handleClosePanel} />}
      </Stack>

      {/* Bottom-right control pane */}
      <Stack
        gap="xs"
        align="flex-end"
        style={{
          position: 'absolute',
          bottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
          right: 'max(16px, env(safe-area-inset-right, 16px))',
          zIndex: 100,
        }}
      >
        {controlPaneContent}
        <CameraControls onResetCamera={handleResetCamera} onTopDownView={handleTopDownView} />
      </Stack>
    </div>
  );
}
